import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { loginSchema } from '@mindpeer/auth-shared';
import { AuthLayout } from '@/components/auth-layout';
import {
  Button,
  DarkAlert,
  DarkCheckbox,
  DarkDivider,
  DarkField,
  DarkInput,
  DarkSubmit,
  FieldIcons,
  GoogleMark,
  ModuleBanner,
  SecurityNote,
} from '@/components/ui';
import { useAuth } from '@/hooks/use-auth';
import { useAuthRequest } from '@/hooks/use-auth-request';
import { useZodForm } from '@/hooks/use-zod-form';
import { googleErrorFrom, startGoogleSignIn } from '@/lib/google-sign-in';
import { destinationFrom } from '@/lib/redirect';

export function SignInPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const destination = destinationFrom(location.search);
  const authRequest = useAuthRequest();
  // Set by the server when it sends the browser back from a Google sign-in
  // that did not work out. Read on every render rather than held in state:
  // the page is freshly mounted by that redirect, so there is no earlier
  // render for an effect to update.
  const googleError = googleErrorFrom(location.search);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Someone who is already signed in has no business on this page — but if they
  // arrived mid-authorization, "already signed in" is exactly the shared sign-on
  // case, so hand straight back to /authorize instead of the account page.
  useEffect(() => {
    if (!user) return;
    if (authRequest.resume()) return;
    navigate(destination, { replace: true });
  }, [user, destination, navigate, authRequest]);

  // A module may pass `login_hint`; /authorize forwards it as `email`.
  const suggestedEmail = new URLSearchParams(location.search).get('email') ?? '';

  const form = useZodForm({
    schema: loginSchema,
    initialValues: { email: suggestedEmail, password: '', rememberMe: false },
    onSubmit: async (input) => {
      const result = await login(input);

      // The server has already told us why the account cannot proceed, so route
      // on its answer rather than re-deriving it (PRD §6.2, §7.2). The handle
      // travels with them so the module's request survives the detour.
      if (result.requiresEmailVerification) {
        navigate(authRequest.link('/verify-email'), { replace: true });
        return;
      }
      if (result.awaitingOrganization) {
        navigate(authRequest.link('/awaiting-approval'), { replace: true });
        return;
      }
      if (authRequest.resume()) return;
      navigate(destination, { replace: true });
    },
  });

  /**
   * Hand off to the server, which parks everything this screen knows in a
   * signed cookie before redirecting to Google. Nothing has to survive in the
   * callback URL, so the module's handle never leaves our own origin.
   */
  const signInWithGoogle = () => {
    setGoogleLoading(true);
    startGoogleSignIn({
      next: destination,
      handle: authRequest.handle,
      rememberMe: form.values.rememberMe,
      email: form.values.email || suggestedEmail,
    });
  };

  return (
    <AuthLayout
      title="Welcome back"
      description="Sign in to access defensible market intelligence and drive smarter advisory outcomes."
      aside={<SecurityNote>Enterprise-grade security. Your data is protected.</SecurityNote>}
      footer={
        <>
          Don&apos;t have an account?{' '}
          <Link
            to={authRequest.link('/sign-up')}
            className="cursor-pointer font-medium text-brand-400 hover:text-brand-300"
          >
            Sign up
          </Link>
          <span className="mx-3 text-white/15">|</span>
          <a
            href="mailto:support@mindpeer.com"
            className="cursor-pointer font-medium text-brand-400 hover:text-brand-300"
          >
            Contact support
          </a>
        </>
      }
    >
      <ModuleBanner applicationName={authRequest.applicationName} />

      {googleError && (
        <div className="mb-4">
          <DarkAlert tone="error">{googleError}</DarkAlert>
        </div>
      )}

      <Button
        variant="onDark"
        fullWidth
        loading={googleLoading}
        onClick={signInWithGoogle}
        className="py-2.5"
      >
        <GoogleMark />
        Continue with Google
      </Button>

      <div className="my-5">
        <DarkDivider label="or" />
      </div>

      <form onSubmit={form.handleSubmit} noValidate className="space-y-5">
        {form.formError && <DarkAlert tone="error">{form.formError}</DarkAlert>}

        <DarkField label="Email" htmlFor="field-email" error={form.errorFor('email')}>
          <DarkInput
            {...form.fieldProps('email')}
            onBlur={undefined}
            type="email"
            icon={FieldIcons.mail}
            placeholder="you@company.com"
            autoComplete="username"
            required
            value={form.values.email}
            onChange={(event) => form.setValue('email', event.target.value)}
          />
        </DarkField>

        <DarkField
          label="Password"
          htmlFor="field-password"
          error={form.errorFor('password')}
          action={
            <Link
              to={authRequest.link('/forgot-password')}
              className="text-xs font-medium text-brand-400 hover:text-brand-300"
            >
              Forgot password?
            </Link>
          }
        >
          <DarkInput
            {...form.fieldProps('password')}
            type="password"
            icon={FieldIcons.lock}
            placeholder="••••••••"
            autoComplete="current-password"
            required
            value={form.values.password}
            onChange={(event) => form.setValue('password', event.target.value)}
          />
        </DarkField>

        <DarkCheckbox
          id="field-rememberMe"
          label="Remember me"
          checked={form.values.rememberMe}
          onChange={(event) => form.setValue('rememberMe', event.target.checked)}
        />

        <DarkSubmit lock loading={form.submitting}>
          Sign in
        </DarkSubmit>
      </form>
    </AuthLayout>
  );
}
